import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import '../../../Common/ShimmerLoader.css'; // Ensure shimmer styles are loaded
import { SellerSuccessModal } from './components/SellerSuccessModal';
import { getSellers, updateSeller } from '../../../api/shop';
import { Seller } from '../../../api/shop/types/sellerTypes';
import SellerCard from './components/SellerCard';
import SellerFormModal from './components/SellerFormModal';
import { SearchBar, FilterDropdown, ActionButton, FilterContainer } from '../../../components/ShopServices/Filters';
import Pagination from '../Orders/components/Pagination';
import '../../../Common/BadgeStyles.css'; // Ensure badge styles are loaded
import { useSelector } from 'react-redux';
import { hasAnyPermission, type User } from '../../../utils/roleBasedAccess';
import './Sellers.css';

const SellersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, [
    'shop.admin',
    'shop.seller.manage',
  ]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'ACTIVE' | 'INACTIVE'>('All');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<Seller | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isUpdateOperation, setIsUpdateOperation] = useState(false);

  const fetchSellers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getSellers({
        pageNum: page,
        status: statusFilter,
      });
      setSellers(response.paginatedResults || []);
      setTotalPages(response.totalPages || 1);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  const handleAddSeller = () => {
    if (!hasManagePermissions) return;
    setSelectedSeller(null);
    setShowFormModal(true);
  };

  const handleEditSeller = (seller: Seller) => {
    if (!hasManagePermissions) return;
    setSelectedSeller(seller);
    setShowFormModal(true);
  };

  const handleViewSeller = (seller: Seller) => {
    navigate(`/shop-services/sellers/${seller._id}`);
  };

  const handleToggleStatus = async (seller: Seller) => {
    if (!hasManagePermissions) return;
    const newStatus = seller.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    
    // Optimistically update the local state immediately
    setSellers(prevSellers => 
      prevSellers.map(s => 
        s._id === seller._id ? { ...s, status: newStatus } : s
      )
    );
    
    try {
      // Update on server
      await updateSeller(seller._id, {
        ...seller,
        status: newStatus,
      });
    } catch (error) {
      // Revert on error - refetch to sync with server state
      fetchSellers();
    }
  };

  const handleFormSuccess = () => {
    const wasUpdate = selectedSeller !== null;
    setIsUpdateOperation(wasUpdate);
    setSuccessMessage(wasUpdate ? 'Seller has been updated successfully' : 'Seller has been created successfully');
    setShowFormModal(false);
    setSelectedSeller(null);
    setShowSuccessModal(true);
    fetchSellers();
  };

  const filteredSellers = sellers.filter(seller => {
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        seller.name?.toLowerCase().includes(searchLower) ||
        seller.id?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className="page-content" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={[
            { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
            { label: 'Sellers', active: true }
          ]} />
        </div>

        {/* Header Section */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <Row className="align-items-center">
            <Col md={6}>
              <h1 className="page-title mb-0">Sellers</h1>
            </Col>
            <Col md={6}>
              <FilterContainer>
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  placeholder="Search"
                  width="200px"
                />
                <FilterDropdown
                  value={{
                    value: statusFilter,
                    label: statusFilter === 'All' ? 'All' : statusFilter === 'ACTIVE' ? 'Active' : 'Inactive'
                  }}
                  onChange={(option) => {
                    if (option) {
                      setStatusFilter(option.value as 'All' | 'ACTIVE' | 'INACTIVE');
                      setPage(1);
                    }
                  }}
                  options={[
                    { value: 'All', label: 'All' },
                    { value: 'ACTIVE', label: 'Active' },
                    { value: 'INACTIVE', label: 'Inactive' }
                  ]}
                  placeholder="Select status..."
                  width="150px"
                />
                {hasManagePermissions && (
                  <ActionButton onClick={handleAddSeller}>
                    List Seller +
                  </ActionButton>
                )}
              </FilterContainer>
            </Col>
          </Row>
        </div>

        {/* Content Section */}
        <div style={{ padding: '0 15px' }}>
          {loading ? (
            <Row className="sellers-grid">
              {[...Array(6)].map((_, index) => (
                <Col key={index} md={6} lg={4} className="mb-4">
                  <div className="card" style={{ padding: '20px', minHeight: '200px' }}>
                    <div className="placeholder" style={{ height: '180px', borderRadius: '8px' }}></div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : filteredSellers.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No sellers found</p>
            </div>
          ) : (
            <Row className="sellers-grid">
              {filteredSellers.map((seller) => (
                <Col key={seller._id} md={6} lg={4} className="mb-4">
                  <SellerCard
                    seller={seller}
                    onEdit={hasManagePermissions ? () => handleEditSeller(seller) : undefined}
                    onView={() => handleViewSeller(seller)}
                    onToggleStatus={
                      hasManagePermissions ? () => handleToggleStatus(seller) : undefined
                    }
                  />
                </Col>
              ))}
            </Row>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={10}
              onPageChange={(newPage) => {
                setPage(newPage);
              }}
              onPageSizeChange={(newPageSize) => {
                // Note: Sellers API uses fixed page size of 10
                setPage(1); // Reset to first page when page size changes
              }}
            />
          )}
        </div>
      </Container>

      {/* Form Modal */}
      {showFormModal && (
        <SellerFormModal
          show={showFormModal}
          onHide={() => {
            setShowFormModal(false);
            setSelectedSeller(null);
          }}
          seller={selectedSeller}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Success Modal */}
      <SellerSuccessModal
        show={showSuccessModal}
        handleClose={() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
          setIsUpdateOperation(false);
        }}
        title={isUpdateOperation ? 'Seller Updated Successfully' : 'Seller Created Successfully'}
        message={successMessage}
      />
    </div>
  );
};

export default SellersPage;
